import React, { useState, useEffect, useCallback } from 'react';
import { Users, Globe, Star, TrendingUp } from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const EmptyState = ({ message }) => (
  <div className="text-center py-12 text-gray-400">
    <Users size={36} className="mx-auto mb-3 opacity-30" />
    <p className="text-sm">{message}</p>
  </div>
);

export default function CommunitiesAnalyticsPage({ timeRange = '30d' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardAPI.getCommunitiesAnalytics(timeRange);
      if (res.success) setData(res.data);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [timeRange]);

  useEffect(() => { load(); }, [load]);

  const hasMembers = data && data.totalMembers > 0;
  const hasCommunities = data && data.totalCommunities > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <Globe className="text-green-600" size={24} />
          Communities Analytics
        </h1>
        <p className="text-sm text-gray-500">Think Tanks and community engagement</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Communities', value: data?.totalCommunities ?? 0, icon: Globe, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Total Members', value: data?.totalMembers ?? 0, icon: Users, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'New This Period', value: data?.newMembersInRange ?? 0, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'New Communities', value: data?.newCommunities ?? 0, icon: Star, color: 'text-purple-600', bg: 'bg-purple-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
            <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center mb-2`}>
              <Icon size={16} className={color} />
            </div>
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>
              {loading ? '—' : value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top communities list */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Top Communities by Members
          </h3>
          {loading ? (
            <div className="space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="animate-pulse h-8 bg-gray-100 rounded-lg" />
              ))}
            </div>
          ) : !hasCommunities ? (
            <EmptyState message="No communities found." />
          ) : !hasMembers ? (
            <div className="space-y-3">
              {(data.topCommunities || []).map((c, i) => (
                <div key={c.id} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-5 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    <p className="text-xs text-gray-400">No members yet</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-400">0</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {(data.topCommunities || []).slice(0, 8).map((c, i) => (
                <div key={c.id} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-5 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    <div className="h-1.5 bg-gray-100 rounded-full mt-1">
                      <div
                        className="h-1.5 bg-green-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, (c.members / (data.topCommunities[0]?.members || 1)) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-gray-600">{c.members}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Member growth chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Membership Growth
          </h3>
          {loading ? (
            <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
          ) : !hasMembers ? (
            <EmptyState message="Membership data will appear here once users join communities." />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.perDay}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="newMembers" fill="#16a34a" radius={[3,3,0,0]} name="New Members" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Info banner */}
      {!loading && !hasMembers && hasCommunities && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <strong>{data.totalCommunities} communities exist</strong> but the communities_members
          table has 0 records. Members may be tracked differently in the mobile app —
          worth checking with the RAG service team.
        </div>
      )}
    </div>
  );
}